import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronRight, Code, Smartphone, Cloud, Brain, Check, ArrowRight, ChevronDown, Monitor, Briefcase, Linkedin, Github, Twitter, Facebook, Instagram, Youtube } from 'lucide-react';
import siteLogo from '../assets/logo.png';
import ScrollRevealSection from '../components/ScrollRevealSection';
import FlipCard from '../components/FlipCard';
import Reveal from '../components/Reveal';
import useParticles from '../hooks/useParticles';
import '../App.css';
import { getSettings, getMetrics, getIndustries, getTechStack } from '../services/api';

/* ─── Count-up number ──────────────────────────────────────────────────── */
function StatNumber({ value, visible }) {
  const parsed = React.useMemo(() => {
    const m = value.match(/^(\d+\.?\d*)(.*)$/);
    if (!m) return null;
    return { num: parseFloat(m[1]), isFloat: m[1].includes('.'), suffix: m[2] };
  }, [value]);
  const [display, setDisplay] = useState(parsed ? ('0' + parsed.suffix) : value);
  useEffect(() => {
    if (!visible || !parsed) return;
    const { num, isFloat, suffix } = parsed;
    const steps = 60; const duration = 1400; let step = 0;
    const id = setInterval(() => {
      step++;
      if (step >= steps) { clearInterval(id); setDisplay(value); return; }
      const eased = 1 - Math.pow(1 - step / steps, 3);
      setDisplay((isFloat ? (num * eased).toFixed(1) : Math.floor(num * eased)) + suffix);
    }, duration / steps);
    return () => clearInterval(id);
  }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps
  return <>{display}</>;
}

export default function TechPortfolio({ embedded = false }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [cursorVariant, setCursorVariant] = useState('default');
  const [showServicesDropdown, setShowServicesDropdown] = useState(false);
  const [showAboutDropdown, setShowAboutDropdown] = useState(false);
  const location = useLocation();
  const canvasRef = useRef(null);
  const heroRef = useRef(null);
  const indsOuterRef = useRef(null);
  const indsListRef = useRef(null);
  const [outerExtraH, setOuterExtraH] = useState(640);
  const metricsRef = useRef(null);
  const [metricsVisible, setMetricsVisible] = useState(false);
  const [settings, setSettings] = useState(null);
  const [metrics, setMetrics] = useState([
    { value: "150+", label: "Projects Delivered" },
    { value: "80+", label: "Happy Clients" },
    { value: "5+", label: "Years Experience" },
    { value: "98%", label: "Client Satisfaction" }
  ]);
  const [industries, setIndustries] = useState([
    { title: 'Healthcare & Wellness', desc: 'Patient platforms, clinical workflows, and telehealth systems, built with compliance and data privacy as defaults.' },
    { title: 'Fintech & Payments', desc: 'Payment gateways, banking dashboards, and real-time analytics tools engineered for security and regulatory standards.' },
    { title: 'E-commerce & Retail', desc: 'High-converting storefronts, inventory systems, and loyalty platforms designed to perform under serious load.' },
    { title: 'SaaS & Productivity', desc: 'Multi-tenant platforms and automation software that scales cleanly from your first user to your ten-thousandth.' },
    { title: 'EdTech & Learning', desc: 'Learning management systems, course platforms, and AI tutoring tools that drive completion rates, not just signups.' },
    { title: 'Logistics & Operations', desc: 'Fleet tracking, supply chain visibility, and warehouse systems where milliseconds and accuracy both matter.' },
    { title: 'Real Estate & PropTech', desc: 'Property platforms, listing portals, and CRM integrations that help buyers, agents, and developers close faster.' },
  ]);
  const [techStack, setTechStack] = useState([
    { slug: 'react',       name: 'React',        color: '61DAFB' },
    { slug: 'flutter',     name: 'Flutter',      color: '54C5F8' },
    { slug: 'nextdotjs',   name: 'Next.js',      color: 'ffffff' },
    { slug: 'nodedotjs',   name: 'Node.js',      color: '5FA04E' },
    { slug: 'typescript',  name: 'TypeScript',   color: '3178C6' },
    { slug: 'python',      name: 'Python',       color: 'FFD43B' },
    { slug: 'firebase',    name: 'Firebase',     color: 'FFCA28' },
    { slug: 'postgresql',  name: 'PostgreSQL',   color: '4169E1' },
    { slug: 'mongodb',     name: 'MongoDB',      color: '47A248' },
    { slug: 'docker',      name: 'Docker',       color: '2496ED' },
    { slug: 'figma',       name: 'Figma',        color: 'F24E1E' },
    { slug: 'tailwindcss', name: 'Tailwind CSS', color: '06B6D4' },
  ]);
  useParticles(canvasRef, heroRef);

  // Measure list scrollable height after mount and set outer div height accordingly
  useEffect(() => {
    const timer = setTimeout(() => {
      const list = indsListRef.current;
      if (!list) return;
      const scrollable = list.scrollHeight - list.clientHeight;
      setOuterExtraH(Math.max(scrollable, 0));
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleIndsScroll = () => {
      const outer = indsOuterRef.current;
      const list = indsListRef.current;
      if (!outer || !list) return;
      const scrollableOuter = outer.offsetHeight - window.innerHeight;
      if (scrollableOuter <= 0) return;
      const progress = Math.max(0, Math.min(1, -outer.getBoundingClientRect().top / scrollableOuter));
      list.scrollTop = progress * (list.scrollHeight - list.clientHeight);
    };
    window.addEventListener('scroll', handleIndsScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleIndsScroll);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const el = metricsRef.current;
    if (!el) return;
    const ob = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setMetricsVisible(true); ob.disconnect(); } },
      { threshold: 0.2 }
    );
    ob.observe(el);
    return () => ob.disconnect();
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    if (!location.hash) {
      return;
    }

    const section = location.hash.replace('#', '');
    const timer = setTimeout(() => {
      document.getElementById(section)?.scrollIntoView({ behavior: 'smooth' });
    }, 60);

    return () => clearTimeout(timer);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    getSettings().then(setSettings).catch(() => {});
    getMetrics().then(setMetrics).catch(() => {});
    getIndustries().then(data =>
      setIndustries(data.map(d => ({ ...d, desc: d.description || d.desc || '' })))
    ).catch(() => {});
    getTechStack().then(setTechStack).catch(() => {});
  }, []);

  const services = [
    {
      slug: 'ai-automation',
      title: 'AI & Automation',
      description: 'Intelligent systems that automate workflows, unlock data insights, and give your business a competitive edge.',
      icon: <Brain className="w-5 h-5" />,
      image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=600&h=400&fit=crop'
    },
    {
      slug: 'custom-software',
      title: 'Custom Software',
      description: 'Bespoke software built around your exact workflows, internal tools, platforms, and systems engineered from the ground up.',
      icon: <Code className="w-5 h-5" />,
      image: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=600&h=400&fit=crop'
    },
    {
      slug: 'web-development',
      title: 'Web Development',
      description: 'Scalable, high-performance web applications built with modern frameworks and best engineering practices.',
      icon: <Monitor className="w-5 h-5" />,
      image: 'https://images.unsplash.com/photo-1547658719-da2b51169166?w=600&h=400&fit=crop'
    },
    {
      slug: 'mobile-app-development',
      title: 'Mobile App Development',
      description: 'Native and cross-platform iOS and Android apps with exceptional UX and performance.',
      icon: <Smartphone className="w-5 h-5" />,
      image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=600&h=400&fit=crop'
    },
    {
      slug: 'cloud-backend',
      title: 'Cloud & Backend',
      description: 'Robust cloud infrastructure and backend architectures engineered to scale reliably with your growth.',
      icon: <Cloud className="w-5 h-5" />,
      image: 'https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=600&h=400&fit=crop'
    }
  ];


  const whyChooseUs = [
    {
      title: "Scalable Architecture",
      description: "Future-proof solutions designed to grow with your business needs."
    },
    {
      title: "Modern Tech Stack",
      description: "Leveraging cutting-edge technologies for optimal performance."
    },
    {
      title: "Client-Centric Approach",
      description: "Your success is our priority. We listen, adapt, and deliver."
    },
    {
      title: "Performance Optimized",
      description: "Speed and efficiency built into every line of code."
    }
  ];

  const scrollToSection = (section) => {
    setMobileMenuOpen(false);
    setShowServicesDropdown(false);
    setShowAboutDropdown(false);
    document.getElementById(section)?.scrollIntoView({ behavior: 'smooth' });
  };


  return (
    <div className={`min-h-screen bg-slate-950 text-white overflow-hidden ${embedded ? '' : 'cursor-none'}`}>
      {/* Custom Cursor */}
      {!embedded && (
        <>
          <div 
            className="hidden lg:block fixed w-8 h-8 pointer-events-none z-[9999] mix-blend-difference"
            style={{ left: `${mousePosition.x}px`, top: `${mousePosition.y}px`, transform: 'translate(-50%, -50%)' }}
          >
            <div className={`w-full h-full rounded-full border-2 border-cyan-400 ${cursorVariant === 'hover' ? 'scale-150' : 'scale-100'} transition-transform duration-200`}></div>
          </div>
          <div 
            className="hidden lg:block fixed w-2 h-2 bg-cyan-400 rounded-full pointer-events-none z-[9998]"
            style={{ left: `${mousePosition.x}px`, top: `${mousePosition.y}px`, transform: 'translate(-50%, -50%)' }}
          ></div>
        </>
      )}

      {/* Navigation */}
      {!embedded && (
      <nav className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-slate-950/95 backdrop-blur-lg shadow-lg shadow-blue-500/5' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <img src={siteLogo} alt="Codinity" style={{ height: '112px', width: 'auto' }} className="object-contain" />
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              <button
                onClick={() => scrollToSection('home')}
                onMouseEnter={() => setCursorVariant('hover')}
                onMouseLeave={() => setCursorVariant('default')}
                className="text-slate-300 hover:text-blue-400 transition-colors duration-300 font-medium bg-transparent border-none cursor-pointer"
              >
                Home
              </button>

              {/* ── SERVICES MEGA DROPDOWN ── */}
              <div
                className="relative"
                onMouseEnter={() => { setCursorVariant('hover'); setShowServicesDropdown(true); }}
                onMouseLeave={() => { setCursorVariant('default'); setShowServicesDropdown(false); }}
              >
                <button className="text-slate-300 hover:text-blue-400 transition-colors duration-300 font-medium flex items-center gap-1 bg-transparent border-none cursor-pointer py-8">
                  Services
                  <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${showServicesDropdown ? 'rotate-180' : ''}`} />
                </button>

                {showServicesDropdown && (
                  <div
                    className="absolute top-full pt-3"
                    style={{
                      left: '50%',
                      transform: 'translateX(-55%)',
                      width: '720px',
                    }}
                  >
                    <div className="bg-slate-900/95 border border-slate-700/80 rounded-2xl shadow-2xl shadow-black/50 p-6">
                      <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-800">
                        <span className="text-xs font-bold text-blue-400 uppercase tracking-widest">Our Services</span>
                        <Link to="/services" onClick={() => setShowServicesDropdown(false)} className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1 no-underline">
                          View all
                          <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                      </div>
                      <div className="grid grid-cols-2 gap-2.5">
                        {services.map((service) => (
                          <Link
                            key={service.slug}
                            to={`/services/${service.slug}`}
                            onClick={() => setShowServicesDropdown(false)}
                            className="flex items-start gap-3 p-3 rounded-xl bg-slate-800/30 hover:bg-slate-800 border border-transparent hover:border-blue-500/30 transition-all duration-200 group no-underline"
                          >
                            <div className="w-9 h-9 bg-blue-500/10 group-hover:bg-blue-500/20 rounded-lg flex items-center justify-center text-blue-400 flex-shrink-0 transition-colors">
                              {service.icon}
                            </div>
                            <div>
                              <div className="text-sm font-bold text-white group-hover:text-blue-400 transition-colors leading-tight">{service.title}</div>
                              <div className="text-xs text-slate-400 mt-0.5 leading-snug">{service.description}</div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* ── ABOUT MEGA DROPDOWN ── */}
              <div
                className="relative"
                onMouseEnter={() => { setCursorVariant('hover'); setShowAboutDropdown(true); }}
                onMouseLeave={() => { setCursorVariant('default'); setShowAboutDropdown(false); }}
              >
                <button className="text-slate-300 hover:text-blue-400 transition-colors duration-300 font-medium flex items-center gap-1 bg-transparent border-none cursor-pointer py-8">
                  About
                  <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${showAboutDropdown ? 'rotate-180' : ''}`} />
                </button>

                {showAboutDropdown && (
                  <div
                    className="absolute top-full pt-3"
                    style={{
                      left: '50%',
                      transform: 'translateX(-55%)',
                      width: '680px',
                    }}
                  >
                    <div className="bg-slate-900/95 border border-slate-700/80 rounded-2xl shadow-2xl shadow-black/50 flex p-6 gap-5"
                      style={{ minHeight: '240px' }}
                    >
                      {/* LEFT RAIL */}
                      <div className="bg-slate-950/80 border border-slate-800 rounded-xl flex flex-col py-5" style={{ width: '190px', flexShrink: 0 }}>
                        <div className="px-5 pb-3 mb-1">
                          <span className="text-xs font-bold text-blue-400 uppercase tracking-widest">Explore</span>
                        </div>
                        {[
                          { label: 'Company', id: 'company' },
                          { label: 'Join Us', id: 'join' },
                        ].map((tab) => (
                          <div key={tab.id} className="px-5 py-3">
                            <div className="text-sm font-semibold text-slate-100 border-l-2 border-blue-400 pl-3">{tab.label}</div>
                          </div>
                        ))}
                      </div>

                      {/* RIGHT PANEL */}
                      <div className="flex-1 bg-slate-950/40 border border-slate-800 rounded-xl p-6">
                        {/* Company section */}
                        <div className="mb-5">
                          <div className="text-xs font-bold text-blue-400 uppercase tracking-widest mb-3">Company</div>
                          <div className="grid grid-cols-1 gap-1.5">
                            {[
                              { label: 'About Codinity', sub: 'Our story and mission', section: 'about' },
                              { label: 'Testimonials', sub: 'What clients say', section: 'testimonials' },
                              { label: 'Our Work', sub: 'Featured projects', section: 'work' },
                            ].map((item) => (
                              <button
                                key={item.label}
                                onClick={() => { scrollToSection(item.section); setShowAboutDropdown(false); }}
                                className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-left hover:bg-slate-800 border border-transparent hover:border-blue-500/20 transition-all duration-150 cursor-pointer group w-full bg-transparent"
                              >
                                <div className="w-2 h-2 rounded-full bg-blue-500/40 group-hover:bg-blue-400 transition-colors flex-shrink-0"></div>
                                <div>
                                  <div className="text-sm font-semibold text-white group-hover:text-blue-400 transition-colors">{item.label}</div>
                                  <div className="text-xs text-slate-400 mt-0.5">{item.sub}</div>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="border-t border-slate-800 pt-5">
                          <div className="text-xs font-bold text-blue-400 uppercase tracking-widest mb-3">Join Us</div>
                          <div className="grid grid-cols-1 gap-1.5">
                            <Link
                              to="/careers"
                              onClick={() => setShowAboutDropdown(false)}
                              className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-left hover:bg-slate-800 border border-transparent hover:border-blue-500/20 transition-all duration-150 no-underline group"
                            >
                              <div className="w-2 h-2 rounded-full bg-blue-500/40 group-hover:bg-blue-400 transition-colors flex-shrink-0"></div>
                              <div>
                                <div className="flex items-center gap-2 text-sm font-semibold text-white group-hover:text-blue-400 transition-colors">
                                  <Briefcase className="w-3.5 h-3.5" /> Careers
                                </div>
                                <div className="text-xs text-slate-400 mt-0.5">Open positions</div>
                              </div>
                            </Link>
                            <button
                              onClick={() => { scrollToSection('contact'); setShowAboutDropdown(false); }}
                              className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-left hover:bg-slate-800 border border-transparent hover:border-blue-500/20 transition-all duration-150 cursor-pointer group w-full bg-transparent"
                            >
                              <div className="w-2 h-2 rounded-full bg-blue-500/40 group-hover:bg-blue-400 transition-colors flex-shrink-0"></div>
                              <div>
                                <div className="text-sm font-semibold text-white group-hover:text-blue-400 transition-colors">Contact Us</div>
                                <div className="text-xs text-slate-400 mt-0.5">Get in touch</div>
                              </div>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <button
                onClick={() => scrollToSection('contact')}
                onMouseEnter={() => setCursorVariant('hover')}
                onMouseLeave={() => setCursorVariant('default')}
                className="text-slate-300 hover:text-blue-400 transition-colors duration-300 font-medium bg-transparent border-none cursor-pointer"
              >
                Contact
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-white p-2 bg-transparent border-none cursor-pointer"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-slate-900/95 backdrop-blur-lg border-t border-slate-800">
            <div className="px-4 py-6 space-y-4">
              <button onClick={() => scrollToSection('home')} className="block text-slate-300 hover:text-blue-400 transition-colors duration-300 font-medium py-2 w-full text-left bg-transparent border-none">Home</button>
              <button onClick={() => scrollToSection('services')} className="block text-slate-300 hover:text-blue-400 transition-colors duration-300 font-medium py-2 w-full text-left bg-transparent border-none">Services</button>
              <button onClick={() => scrollToSection('about')} className="block text-slate-300 hover:text-blue-400 transition-colors duration-300 font-medium py-2 w-full text-left bg-transparent border-none">About</button>
              <Link to="/careers" className="block text-slate-300 hover:text-blue-400 transition-colors duration-300 font-medium py-2 w-full text-left no-underline">Careers</Link>
              <button onClick={() => scrollToSection('contact')} className="block text-slate-300 hover:text-blue-400 transition-colors duration-300 font-medium py-2 w-full text-left bg-transparent border-none">Contact</button>
            </div>
          </div>
        )}
      </nav>
      )}

      {/* Hero Section */}
      <section ref={heroRef} id="home" className={`relative min-h-screen flex items-center justify-center overflow-hidden ${embedded ? 'pt-0' : 'pt-20'}`}>
        <canvas ref={canvasRef} className="particle-canvas" />
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 -left-20 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,#000_70%,transparent_100%)]"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="space-y-8 animate-fade-in">
            <div className="inline-block">
              <span className="px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full text-blue-400 text-sm font-semibold backdrop-blur-sm tracking-wide uppercase">
                {settings?.hero?.badge || 'Premium Software Development'}
              </span>
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight">
              Where Innovation
              <br />
              <span className="bg-gradient-to-r from-blue-400 via-blue-500 to-purple-600 bg-clip-text text-transparent">
                Meets Execution
              </span>
            </h1>
            <p className="text-xl text-slate-400 max-w-3xl mx-auto leading-relaxed">
              {settings?.hero?.subheadline || 'Transforming ideas into scalable, high-performance software solutions. We craft mobile apps, web platforms, and AI-powered systems that drive business growth.'}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
              <button 
                onClick={() => scrollToSection('contact')}
                onMouseEnter={() => setCursorVariant('hover')}
                onMouseLeave={() => setCursorVariant('default')}
                className="group bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 px-8 py-4 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 shadow-xl shadow-blue-500/25 flex items-center gap-2 border-none cursor-pointer text-white"
              >
                {settings?.hero?.primaryCTA || 'Start Your Project'}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={() => scrollToSection('services')}
                onMouseEnter={() => setCursorVariant('hover')}
                onMouseLeave={() => setCursorVariant('default')}
                className="group px-8 py-4 rounded-lg font-semibold border-2 border-slate-700 hover:border-blue-500 transition-all duration-300 flex items-center gap-2 bg-transparent cursor-pointer text-white hover:text-blue-400"
              >
                {settings?.hero?.secondaryCTA || 'View Our Services'}
                <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
            <div ref={metricsRef} className="grid grid-cols-2 md:grid-cols-4 gap-8 pt-16">
              {metrics.map((metric, index) => (
                <div key={index} className="text-center">
                  <div className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent">
                    <StatNumber value={metric.value} visible={metricsVisible} />
                  </div>
                  <div className="text-slate-400 mt-2">{metric.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <ScrollRevealSection id="services" className="py-24 relative min-h-screen flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <Reveal direction="up">
            <div className="text-center mb-16">
              <span className="text-blue-400 font-semibold text-sm uppercase tracking-wider">Our Services</span>
              <h2 className="text-4xl sm:text-5xl font-bold mt-4 mb-6">Comprehensive Digital Solutions</h2>
              <p className="text-slate-400 text-lg max-w-2xl mx-auto">
                From concept to deployment, we deliver end-to-end software solutions tailored to your business needs.
              </p>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6 mb-8">
            {services.map((service, index) => (
              <Reveal key={index} direction="up" delay={index * 70}>
              <FlipCard
                key={index}
                className="rounded-2xl cursor-pointer"
                style={{ height: '16rem' }}
                front={
                  <div className="relative w-full h-full">
                    <img src={service.image} alt={service.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
                    <div className="absolute inset-0 flex items-end p-6">
                      <h3 className="text-lg font-bold text-white">{service.title}</h3>
                    </div>
                  </div>
                }
                back={
                  <div className="w-full h-full bg-gradient-to-br from-slate-900 to-slate-800 border border-blue-500/30 flex flex-col items-center justify-center p-6 text-center gap-4">
                    <div className="text-cyan-400">{service.icon}</div>
                    <h3 className="text-lg font-bold text-white">{service.title}</h3>
                    <p className="text-slate-400 text-sm leading-relaxed">{service.description}</p>
                    <div className="flex items-center gap-2 text-blue-400 text-sm font-semibold mt-2">
                      <span>Learn More</span>
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                }
              />
              </Reveal>
            ))}
          </div>
            <div className="text-center">
              <Link
                to="/services"
                onMouseEnter={() => setCursorVariant('hover')}
                onMouseLeave={() => setCursorVariant('default')}
                className="group bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 px-8 py-4 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 shadow-xl shadow-blue-500/25 inline-flex items-center gap-2 no-underline text-white"
              >
                View All Services
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

        </div>
      </ScrollRevealSection>

      {/* About Section */}
      <ScrollRevealSection id="about" className="py-24 relative min-h-screen flex items-center" bg="bg-slate-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <Reveal direction="left">
            <div>
              <span className="text-blue-400 font-semibold text-sm uppercase tracking-wider">
                {settings?.company?.name ? `About ${settings.company.name}` : 'About Codinity'}
              </span>
              <h2 className="text-4xl sm:text-5xl font-bold mt-4 mb-6">
                {settings?.about?.title || 'Innovating the Future of Technology'}
              </h2>
              <p className="text-slate-400 text-lg leading-relaxed mb-6">
                {settings?.about?.description || 'Codinity is a forward-thinking software development company dedicated to creating cutting-edge digital solutions. We combine technical excellence with creative innovation to deliver products that make a real impact.'}
              </p>
              <p className="text-slate-400 text-lg leading-relaxed mb-8">
                {settings?.about?.mission || 'Our mission is to empower businesses with scalable, reliable, and performance-optimized software that drives growth and competitive advantage in the digital age.'}
              </p>
              <div className="space-y-4">
                {['Innovation-Driven Development', 'Scalable & Reliable Solutions', 'Client Success Focus'].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-blue-400" />
                    </div>
                    <span className="text-slate-300">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            </Reveal>
            <Reveal direction="right">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-purple-600/20 rounded-3xl blur-3xl"></div>
              <div className="relative bg-slate-900/80 backdrop-blur-sm border border-slate-800 rounded-3xl p-8">
                <div className="grid grid-cols-2 gap-6">
                  {whyChooseUs.map((item, index) => (
                    <div
                      key={index}
                      onMouseEnter={() => setCursorVariant('hover')}
                      onMouseLeave={() => setCursorVariant('default')}
                      className="text-center p-6 bg-slate-800/50 rounded-xl hover:bg-slate-800 transition-all duration-300 cursor-pointer group"
                    >
                      <div className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent mb-2 group-hover:scale-110 transition-transform">{index + 1}</div>
                      <div className="text-sm font-semibold text-slate-300 mb-2 group-hover:text-blue-400 transition-colors">{item.title}</div>
                      <div className="text-xs text-slate-500">{item.description}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            </Reveal>
          </div>
        </div>
      </ScrollRevealSection>


      {/* Industries Section — outer tall container drives scroll progress */}
      <div ref={indsOuterRef} style={{ position: 'relative', height: `calc(100vh + ${outerExtraH}px)` }}>
      <section id="work" style={{ position: 'sticky', top: 0, height: '100vh', overflow: 'hidden', background: '#020617', display: 'flex' }}>

        {/* ── LEFT 60% — header + scroll-driven list ── */}
        <div className="industries-left" style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          padding: 'clamp(5rem, 8vw, 7rem) clamp(4rem, 10vw, 9rem) 2rem clamp(6rem, 10vw, 10rem)',
          overflow: 'hidden',
        }}>
          {/* Header */}
          <div style={{ flexShrink: 0, marginBottom: '2.5rem' }}>
            <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#3b82f6', letterSpacing: '0.16em', textTransform: 'uppercase' }}>Industries</span>
            <h2 style={{ fontSize: 'clamp(2rem, 4vw, 3.25rem)', fontWeight: 800, lineHeight: 1.06, color: '#f1f5f9', marginTop: '0.75rem', letterSpacing: '-0.03em' }}>
              Built for<br />
              <span style={{ background: 'linear-gradient(90deg, #60a5fa, #a78bfa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                Your Industry
              </span>
            </h2>
            <p style={{ fontSize: '0.9rem', color: '#475569', marginTop: '1rem', lineHeight: 1.7 }}>
              Domain knowledge across six sectors, we understand the problem before touching the code.
            </p>
          </div>

          {/* Scroll viewport — overflow:scroll driven by page scroll via scrollTop */}
          <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
            {/* fade masks */}
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3rem', background: 'linear-gradient(to bottom, #020617, transparent)', zIndex: 2, pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '5rem', background: 'linear-gradient(to top, #020617, transparent)', zIndex: 2, pointerEvents: 'none' }} />

            {/* overflow-y scroll container — scrollTop controlled via JS, no scrollbar shown */}
            <div
              ref={indsListRef}
              style={{
                height: '100%',
                overflowY: 'scroll',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                pointerEvents: 'none',
              }}
            >
              {industries.map((ind, i) => (
                <div
                  key={i}
                  style={{
                    borderTop: i === 0 ? '1px solid rgba(30,41,59,0.7)' : 'none',
                    borderBottom: '1px solid rgba(30,41,59,0.7)',
                    padding: '2.25rem 0',
                    cursor: 'default',
                  }}
                >
                  <div style={{ display: 'flex', gap: '1.75rem', alignItems: 'flex-start' }}>
                    <span style={{ fontSize: '0.65rem', fontWeight: 800, color: 'rgba(59,130,246,0.25)', letterSpacing: '0.1em', fontFamily: 'monospace', paddingTop: '0.3rem', flexShrink: 0, minWidth: '1.5rem' }}>
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 'clamp(1.1rem, 1.6vw, 1.3rem)', fontWeight: 700, color: '#e2e8f0', marginBottom: '0.55rem', letterSpacing: '-0.01em' }}>
                        {ind.title}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: '#475569', lineHeight: 1.8 }}>
                        {ind.desc}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── RIGHT 40% — full-bleed image ── */}
        <div className="industries-right" style={{ height: '100%', position: 'relative', overflow: 'hidden' }}>
          <img
            src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200&h=1400&fit=crop"
            alt="Industries"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
          {/* left edge fade into dark bg */}
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to right, #020617 0%, rgba(2,6,23,0.2) 40%, transparent 70%)' }} />
          {/* overall darkening grade */}
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(2,6,23,0.2)' }} />
          {/* bottom badge */}
          <div style={{ position: 'absolute', bottom: '2rem', right: '2rem' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.55rem', background: 'rgba(2,6,23,0.75)', backdropFilter: 'blur(14px)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '9999px', padding: '0.45rem 1rem' }}>
              <div style={{ width: '0.4rem', height: '0.4rem', borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 8px #22c55e' }} />
              <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#94a3b8' }}>End-to-end · {industries.length} industries</span>
            </div>
          </div>
        </div>

      </section>
      </div>{/* end indsOuterRef */}

      {/* Tech Stack Section */}
      <ScrollRevealSection className="py-24 relative" style={{ background: '#020617' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">

          <Reveal direction="up">
            <div className="text-center mb-16">
              <span className="text-blue-400 font-semibold text-sm uppercase tracking-wider">Our Stack</span>
              <h2 className="text-4xl sm:text-5xl font-bold mt-4 mb-6">Built with Best-in-Class Tools</h2>
              <p className="text-slate-400 text-lg max-w-2xl mx-auto">
                We work with the technologies that make the most sense for your product, proven, scalable, and battle-tested.
              </p>
            </div>
          </Reveal>

          <div className="tech-marquee-outer">
            <div className="tech-marquee-track">
              {[...techStack, ...techStack].map((t, i) => (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 2.5rem' }}>
                  <img
                    src={`https://cdn.simpleicons.org/${t.slug}/${t.color}`}
                    alt={t.name}
                    title={t.name}
                    style={{ width: '2.75rem', height: '2.75rem' }}
                    onError={e => {
                      const el = e.currentTarget;
                      if (!el.dataset.fallback) {
                        el.dataset.fallback = '1';
                        el.src = `https://cdn.simpleicons.org/${t.slug}`;
                      } else {
                        el.style.visibility = 'hidden';
                      }
                    }}
                  />
                  <span style={{ fontSize: '0.72rem', color: '#64748b', whiteSpace: 'nowrap', fontWeight: 500 }}>{t.name}</span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </ScrollRevealSection>

      {/* Pre-footer CTA */}
      <ScrollRevealSection id="contact" className="py-20 relative flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <Reveal direction="up">
          <div className="rounded-3xl text-center relative overflow-hidden" style={{ padding: '5rem 3rem' }}>
            <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1552664730-d307ca884978?w=1400&h=600&fit=crop)' }}></div>
            <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(15,23,42,0.88) 0%, rgba(30,58,138,0.82) 50%, rgba(88,28,135,0.82) 100%)' }}></div>
            <div className="relative z-10">
              <h2 className="text-4xl font-bold mb-4">Ready to Start Your Project?</h2>
              <p className="text-blue-100 text-lg mb-8 max-w-2xl mx-auto">
                Let's discuss how we can help bring your vision to life with cutting-edge technology solutions.
              </p>
              <Link
                to="/contact"
                onMouseEnter={() => setCursorVariant('hover')}
                onMouseLeave={() => setCursorVariant('default')}
                className="inline-flex items-center gap-2 bg-white text-blue-600 hover:bg-slate-100 px-8 py-4 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 shadow-xl no-underline cursor-pointer"
              >
                Contact Us
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          </Reveal>
        </div>
      </ScrollRevealSection>

      {/* Footer */}
      {!embedded && (
      <footer style={{ background: '#020617', borderTop: '1px solid rgba(30,41,59,0.6)', padding: '4rem 0 2rem' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Main grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '3rem', marginBottom: '3rem' }}>

            {/* Brand */}
            <div style={{ gridColumn: 'span 1' }}>
              <img src={siteLogo} alt="Codinity" style={{ height: '80px', width: 'auto', marginBottom: '1rem' }} className="object-contain" />
              <p style={{ fontSize: '0.85rem', color: '#475569', lineHeight: 1.8, maxWidth: '20rem' }}>
                Building intelligent digital solutions that drive business growth and innovation.
              </p>
            </div>

            {/* Services col 1 */}
            <div>
              <h4 style={{ fontSize: '0.7rem', fontWeight: 700, color: '#3b82f6', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: '1.25rem' }}>Services</h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
                {[
                  { label: 'AI & Automation',        to: '/services/ai-automation' },
                  { label: 'Custom Software',        to: '/services/custom-software' },
                  { label: 'Web Development',         to: '/services/web-development' },
                ].map(s => (
                  <li key={s.label}>
                    <Link to={s.to} style={{ fontSize: '0.875rem', color: '#64748b', textDecoration: 'none', transition: 'color 0.15s' }}
                      onMouseEnter={e => { e.currentTarget.style.color = '#cbd5e1'; setCursorVariant('hover'); }}
                      onMouseLeave={e => { e.currentTarget.style.color = '#64748b'; setCursorVariant('default'); }}>
                      {s.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Services col 2 */}
            <div>
              <h4 style={{ fontSize: '0.7rem', fontWeight: 700, color: 'transparent', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: '1.25rem' }}>_</h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
                {[
                  { label: 'Mobile App Development', to: '/services/mobile-app-development' },
                  { label: 'Cloud & Backend',        to: '/services/cloud-backend' },
                ].map(s => (
                  <li key={s.label}>
                    <Link to={s.to} style={{ fontSize: '0.875rem', color: '#64748b', textDecoration: 'none', transition: 'color 0.15s' }}
                      onMouseEnter={e => { e.currentTarget.style.color = '#cbd5e1'; setCursorVariant('hover'); }}
                      onMouseLeave={e => { e.currentTarget.style.color = '#64748b'; setCursorVariant('default'); }}>
                      {s.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Quick Links */}
            <div>
              <h4 style={{ fontSize: '0.7rem', fontWeight: 700, color: '#3b82f6', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: '1.25rem' }}>Company</h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
                {[
                  { label: 'About Us',  action: () => scrollToSection('about') },
                  { label: 'Services',  action: () => scrollToSection('services') },
                  { label: 'Careers',   action: () => scrollToSection('careers') },
                  { label: 'Contact',   action: () => scrollToSection('contact') },
                ].map(item => (
                  <li key={item.label}>
                    <button onClick={item.action}
                      style={{ fontSize: '0.875rem', color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', padding: 0, transition: 'color 0.15s' }}
                      onMouseEnter={e => { e.currentTarget.style.color = '#cbd5e1'; setCursorVariant('hover'); }}
                      onMouseLeave={e => { e.currentTarget.style.color = '#64748b'; setCursorVariant('default'); }}>
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

          </div>

          {/* Social links */}
          {settings?.social && (
            <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
              {[
                { key: 'linkedin',  Icon: Linkedin,  label: 'LinkedIn' },
                { key: 'github',    Icon: Github,    label: 'GitHub' },
                { key: 'twitter',   Icon: Twitter,   label: 'Twitter' },
                { key: 'facebook',  Icon: Facebook,  label: 'Facebook' },
                { key: 'instagram', Icon: Instagram, label: 'Instagram' },
                { key: 'youtube',   Icon: Youtube,   label: 'YouTube' },
              ].filter(s => settings.social[s.key]).map(({ key, Icon, label }) => (
                <a key={key} href={settings.social[key]} target="_blank" rel="noopener noreferrer" aria-label={label}
                  style={{ width: '2rem', height: '2rem', borderRadius: '0.5rem', background: 'rgba(30,41,59,0.6)', border: '1px solid rgba(51,65,85,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569', textDecoration: 'none', transition: 'all 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.color = '#60a5fa'; e.currentTarget.style.borderColor = 'rgba(59,130,246,0.4)'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = '#475569'; e.currentTarget.style.borderColor = 'rgba(51,65,85,0.4)'; }}>
                  <Icon size={14} />
                </a>
              ))}
            </div>
          )}

          {/* Bottom bar */}
          <div style={{ borderTop: '1px solid rgba(30,41,59,0.6)', paddingTop: '1.75rem', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
            <p style={{ fontSize: '0.8rem', color: '#334155', margin: 0 }}>© {new Date().getFullYear()} {settings?.company?.name || 'Codinity'}. All rights reserved.</p>
          </div>

        </div>
      </footer>
      )}
    </div>
  );
}